// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers. The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import buttonSizeVariants from "@eisinc/MobiusOverrides/Button/buttonSizeVariants";
import { xBaseTheme } from "@eisinc/XDL/types";
import { getSystemColor } from "@eisinc/XDL/Utilities";
import { ButtonIcon } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import get from "@insite/mobius/utilities/get";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

// Button color configurations
const buttonColors: { [key: string]: { [key: string]: string } } = {
    primary: {
        main: "primary.main",
        hover: "common.ui8",
        focus: "common.ui9",
    },
    secondary: {
        main: "primary.main",
        hover: "common.ui8",
        focus: "common.ui9",
    },
    tertiary: {
        main: "text.link",
        hover: "common.ui3",
        focus: "common.ui3",
    },
    link: {
        main: "text.link",
        hover: "common.ui9",
        focus: "common.ui9",
    },
};
const getSizeRelatedStyles = (size: keyof typeof buttonSizeVariants) => {
    return css`
        min-height: ${get(buttonSizeVariants, [size, "height"])}px;
        line-height: ${get(buttonSizeVariants, [size, "lineHeight"])};
        padding: ${get(buttonSizeVariants, [size, "padding"])};
    `;
};

const sharedButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    ${({ _sizeVariant }: any) => getSizeRelatedStyles(_sizeVariant)}

    ${ButtonIcon} {
        top: 0;
    }

    &.small {
        ${getSizeRelatedStyles("small")}
    }
    &.medium {
        ${getSizeRelatedStyles("medium")}
    }
    &.large {
        ${getSizeRelatedStyles("large")}
    }
`;

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.primary.main,
        buttonType: "solid",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor(buttonColors.primary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
            color: `${getSystemColor("common.white")}`,
        },
        typographyProps: {
            color: "common.white",
            weight: 500,
            css: css`
                text-transform: uppercase;
            `,
        },
        css: css`
            border-radius: 0;
            border-width: 1px;
            &:focus,
            &[data-simulate-state="focus"] {
                background: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
            }
            &[data-simulate-state="hover"] {
                background: ${getSystemColor(buttonColors.primary.hover)};
                border-color: ${getSystemColor(buttonColors.primary.hover)};
                border-color: ${getSystemColor(buttonColors.primary.hover)};
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.primary.main, 0.33)};
                border-color: transparent;
            }
            ${IconWrapper} {
                color: ${getSystemColor("common.white")};
            }
            ${sharedButtonStyles}
        `,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.secondary.main,
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.secondary.hover)}`,
            color: `${getSystemColor(buttonColors.secondary.hover)}`,
        },
        typographyProps: {
            weight: 500,
            css: css`
                text-transform: uppercase;
                color: ${getSystemColor(buttonColors.secondary.main)};
            `,
        },
        css: css`
            border-radius: 0;
            border-width: 1px;
            border-color: ${getSystemColor(buttonColors.secondary.main)};
            background-color: ${getSystemColor("common.white")};
            &:focus,
            &[data-simulate-state="focus"] {
                border-color: ${getSystemColor(buttonColors.secondary.focus)};
                color: ${getSystemColor(buttonColors.secondary.focus)};
                ${TypographyStyle} {
                    color: ${getSystemColor(buttonColors.secondary.focus)};
                }
            }
            &:hover,
            &[data-simulate-state="hover"] {
                border-color: ${getSystemColor(buttonColors.secondary.hover)};
                color: ${getSystemColor(buttonColors.secondary.hover)};
                ${TypographyStyle} {
                    color: ${getSystemColor(buttonColors.secondary.hover)};
                }
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.secondary.main, 0.33)};
                color: ${getSystemColor(buttonColors.secondary.main, 0.33)};
                ${TypographyStyle} {
                    color: ${getSystemColor(buttonColors.secondary.main, 0.33)};
                }
            }
            ${sharedButtonStyles}
        `,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.tertiary.main,
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.tertiary.hover)}`,
            color: `${getSystemColor(buttonColors.tertiary.hover)}`,
        },
        typographyProps: {
            weight: 500,
            css: css`
                text-transform: uppercase;
            `,
        },
        css: css`
            border-radius: 0;
            border: 1px solid;
            &:focus,
            &[data-simulate-state="focus"] {
                border-color: ${getSystemColor(buttonColors.tertiary.focus)};
                color: ${getSystemColor(buttonColors.tertiary.focus)};
            }
            &[data-simulate-state="hover"] {
                border-color: ${getSystemColor(buttonColors.secondary.hover)};
                color: ${getSystemColor(buttonColors.secondary.hover)};
                ${TypographyStyle} {
                    color: ${getSystemColor(buttonColors.secondary.focus)};
                }
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.tertiary.main, 0.33)};
                color: ${getSystemColor(buttonColors.tertiary.main, 0.33)};
            }
            ${sharedButtonStyles}
        `,
    },
    link: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.link.main,
        buttonType: "outline",
        mergeCss: true,
        typographyProps: {
            weight: 500,
        },
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.link.hover)}`,
            color: `${getSystemColor(buttonColors.link.hover)}`,
        },
        css: css`
            ${buttonTransparent}
            padding: 0;
            &:focus,
            &[data-simulate-state="focus"] {
                ${buttonTransparent}
            }
        `,
    },
} as xBaseTheme["button"];
